package com.arena_web.ui.components.widgets

import androidx.compose.runtime.*
import com.arena_web.ui.components.buttons.PrimaryButton
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.utils.*
import com.arena_web.utils.Analytics
import com.arena_web.utils.MetaPixel
import com.arena_web.utils.Storage
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import org.jetbrains.compose.web.css.*

private const val COOKIE_CONSENT_KEY = "cookie_consent"

val CookieSlideUpKeyframes = Keyframes {
    from {
        Modifier.translateY(24.px).opacity(0)
    }
    to {
        Modifier.translateY(0.px).opacity(1)
    }
}

/**
 * Compact cookie-consent card centred at the bottom of the screen: 🍪 + short
 * message, Accept as the single primary action, Decline as a quiet text link.
 * Gradient hairline border + glass blur per the design system; analytics only
 * initialises after an explicit Accept.
 */
@Composable
fun CookieConsent(
    modifier: Modifier = Modifier
) {
    // Consent is auto-granted on the visitor's behalf — the banner is never
    // shown. First visit persists "accepted" and boots analytics/pixel; later
    // visits just re-init from the stored choice. (A prior explicit "declined"
    // is still respected.) NB: auto-consent is not GDPR-explicit.
    var isVisible by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        if (!Storage.contains(COOKIE_CONSENT_KEY)) {
            Storage.save(COOKIE_CONSENT_KEY, "accepted")
            Analytics.onConsentGranted()
            MetaPixel.onConsentGranted()
        } else {
            Analytics.initIfConsented()
            MetaPixel.initIfConsented()
        }
    }

    if (!isVisible) return

    val t = LocalAppStrings.current
    val mobile = isMobile()
    // The desktop s() scale clamps to 0.4 on phones — cookie text rendered at
    // ~6px there. Same design px, but phones resolve through --mobile-scale.
    fun sz(px: Number): String = if (mobile) m(px) else s(px)

    Box(
        modifier = modifier
            .position(Position.Fixed)
            .left(0.px)
            .right(0.px)
            .zIndex(999)
            .styleModifier {
                // Bottom-centre: left/right 0 + auto margins (the slide-up
                // animation owns translate, so no translateX(-50%) here).
                // On phones the fixed BottomNavBar (pill ~76px tall, 24px from
                // the edge) lives at the bottom too — sit clear ABOVE it so the
                // card never overlaps the primary nav. Desktop has a sidebar, so
                // the card stays flush at the bottom there.
                property("bottom", if (mobile) m(108) else "0px")
                property("padding", sz(16))
                property("width", "100%")
                property("max-width", if (mobile) m(320) else "320px")
                property("margin-left", "auto")
                property("margin-right", "auto")
            }
            .animation(
                CookieSlideUpKeyframes.toAnimation(
                    duration = Constants.ANIMATION_NORMAL.ms,
                    timingFunction = AnimationTimingFunction.EaseOut
                )
            )
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("border-radius", sz(14))
                    property("padding", "${sz(14)} ${sz(16)}")
                    property("gap", sz(10))
                    property("border", "1px solid transparent")
                    property(
                        "background",
                        "${AppGradients.SurfaceGradient01} padding-box, " +
                            "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
                            "${AppGradients.BorderGradient01} border-box"
                    )
                    property("box-shadow", "0 12px 32px rgba(0, 0, 0, 0.45)")
                    property("backdrop-filter", "blur(12px)")
                    property("-webkit-backdrop-filter", "blur(12px)")
                }
        ) {
            Row(
                modifier = Modifier.styleModifier { property("gap", sz(10)) },
                verticalAlignment = Alignment.CenterVertically
            ) {
                SpanText("🍪", modifier = Modifier.styleModifier { property("font-size", sz(18)) })
                SpanText(
                    t.widgetCookieTitle,
                    modifier = Modifier
                        .styleModifier { property("font-size", sz(14)) }
                        .fontWeight(600)
                        .color(com.arena_web.ui.theme.ThemeVars.Text1)
                )
            }

            SpanText(
                t.widgetCookieMessage,
                modifier = Modifier
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .styleModifier {
                        property("font-size", sz(12))
                        property("line-height", sz(16))
                    }
            )

            Row(
                modifier = Modifier.fillMaxWidth().styleModifier { property("gap", sz(14)) },
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.End
            ) {
                // Quiet decline — a text link, so Accept stays the hero action.
                SpanText(
                    t.widgetCookieDecline,
                    modifier = Modifier
                        .styleModifier { property("font-size", sz(13)) }
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                        .cursor(com.varabyte.kobweb.compose.css.Cursor.Pointer)
                        .styleModifier {
                            property("text-decoration", "underline")
                            property("text-underline-offset", "3px")
                        }
                        .onClick {
                            Storage.save(COOKIE_CONSENT_KEY, "declined")
                            isVisible = false
                        }
                )
                PrimaryButton(
                    text = t.widgetCookieAccept,
                    onClick = {
                        Storage.save(COOKIE_CONSENT_KEY, "accepted")
                        Analytics.onConsentGranted()
                        MetaPixel.onConsentGranted()
                        isVisible = false
                    },
                    size = ButtonSize.SM
                )
            }
        }
    }
}
